<section role="region" aria-labelledby="person-create-heading">
	<h2 id="person-create-heading" lvrPadding [padHorizontal]="32" [padVertical]="0">
		{{ 'institution.person_create.title' | transloco }}
	</h2>
</section>
<form
	[formGroup]="form"
	class="form-container"
	lvrAutoSave="institution-person"
	[entityId]="draftId"
	[autoLoad]="false"
>
	<section class="form-grid">
		<div>
			<!-- Category Dropdown Field -->
			@let catCtrl = form.get('categoryId');
			<lvr-field [label]="'institution.person_create.category' | transloco | appendAsterisk">
				<lvr-searchable-select
					formControlName="categoryId"
					[options]="personTypes()"
					[optionLabel]="personTypeLabel"
					[placeholder]="'base.field.select_please' | transloco"
					[searchPlaceholder]="'base.field.search_placeholder' | transloco"
					[noResultsText]="'base.field.no_results' | transloco"
					[ariaLabel]="'institution.person_create.category' | transloco"
					[required]="true"
					[hasError]="hasError(catCtrl, 'required')"
					[errorMessage]="'base.form.error.category_required' | transloco"
				></lvr-searchable-select>
			</lvr-field>

			<lvr-responsibility
				formControlName="responsibleBranches"
				[branches]="branches()"
				[label]="'institution.persons.edit.responsibleBranchLabel' | transloco"
			></lvr-responsibility>
		</div>

		<!-- Remark Field -->
		@let remarkCtrl = form.get('remark');
		<lvr-field [label]="'institution.person_create.remark' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(remarkCtrl, 'maxLength')">
				<textarea
					matInput
					formControlName="remark"
					[placeholder]="'institution.person_create.remark_sample' | transloco"
					class="taller-textarea"
					[maxlength]="remarkCtrlMaxLength"
				></textarea>
				<mat-hint>{{
					'base.form.hint.max_length' | transloco: { actual: remarkCtrl?.value?.length || 0, max: remarkCtrlMaxLength }
				}}</mat-hint>
				@if (hasError(remarkCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: remarkCtrlMaxLength } }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>
		<!-- Title Field -->
		@let titleCtrl = form.get('title');
		<lvr-field [label]="'institution.person_create.person_title' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(titleCtrl, 'maxLength')">
				<input
					matInput
					formControlName="title"
					[placeholder]="'institution.person_create.person_title_sample' | transloco"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(titleCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 50 } }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>
		<!-- Gender Field -->
		<lvr-field [label]="'institution.person_create.gender' | transloco">
			<mat-form-field lvrInput [attr.aria-label]="'base.field.gender' | transloco">
				<mat-select
					formControlName="genderId"
					[placeholder]="'base.field.select_please' | transloco"
					[errorStateMatcher]="matcher"
				>
					@for (option of genders(); track option.id) {
						<mat-option
							[value]="option.id"
							matTooltipClass="info-tooltip"
							[matTooltip]="
								option.salutation === 'divers u.a.' ? ('client.create_client.gender_hint_divers' | transloco) : null
							"
						>
							{{ option.salutation }}
						</mat-option>
					}
				</mat-select>
			</mat-form-field>
		</lvr-field>
		<!-- Last Name Field -->
		@let lastNameCtrl = form.get('lastName');
		<lvr-field [label]="'institution.person_create.last_name' | transloco | appendAsterisk">
			<mat-form-field lvrInput [lvrError]="hasError(lastNameCtrl, 'maxLength', 'required', 'pattern')">
				<input
					matInput
					formControlName="lastName"
					[placeholder]="'institution.person_create.last_name_sample' | transloco"
					[errorStateMatcher]="matcher"
					aria-required="true"
				/>

				@if (hasError(lastNameCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 50 } }}</mat-error>
				} @else if (hasError(lastNameCtrl, 'required')) {
					<mat-error>{{ 'base.form.error.last_name_required' | transloco }}</mat-error>
				} @else if (hasError(lastNameCtrl, 'pattern')) {
					<mat-error>{{ 'base.form.error.last_name_invalid' | transloco }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>
		<!-- First Name Field -->
		@let firstNameCtrl = form.get('firstName');
		<lvr-field [label]="'institution.person_create.first_name' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(firstNameCtrl, 'maxLength', 'pattern')">
				<input
					matInput
					formControlName="firstName"
					[placeholder]="'institution.person_create.first_name_sample' | transloco"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(firstNameCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 50 } }}</mat-error>
				} @else if (hasError(firstNameCtrl, 'pattern')) {
					<mat-error>{{ 'base.form.error.first_name_invalid' | transloco }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>
		<!-- Address -->
		<lvr-address-completion class="address-completion" formControlName="address"></lvr-address-completion>
	</section>

	<!-- Communication section -->
	@for (item of communicationWayCountArrayKeys(); track $index) {
		<section class="form-grid">
			<h3 lvrPadding class="additional-communication-heading" [padVertical]="0" [padHorizontal]="8">
				@if (communicationWayCount() > 1) {
					<span>{{ 'base.field.contact_point.title' | transloco }}</span>
					<button matIconButton class="btn btn-tertiary" (click)="removeCommunicationWay($index)">
						<mat-icon class="material-symbols-outlined" aria-hidden="true">delete</mat-icon>
					</button>
				} @else {
					<span>{{ 'base.field.contact_point.title' | transloco }}</span>
				}
			</h3>
			<lvr-contact-point
				[contactPointCategories]="contactPointCategories()"
				class="address-completion"
				(values)="updateValues($event, $index)"
			></lvr-contact-point>
		</section>
	}
	<section class="form-grid">
		<!-- Add additional button -->
		<lvr-button
			variant="primary"
			[label]="'base.field.contact_point.title' | transloco"
			[ariaLabel]="'base.field.contact_point.add_communication' | transloco"
			[type]="'button'"
			(clicked)="addCommunicationWay()"
			icon="add"
		></lvr-button>
	</section>

	<!-- Privacy Field -->
	<lvr-data-protection formControlName="clarification" [showAsterisk]="false"></lvr-data-protection>

	<lvr-default-actions
		[disableSave]="form.invalid || !canCreateInstitutionPersonsPermission()"
		[showLoading]="saving()"
		(save)="save()"
		(cancelSave)="cancel()"
	>
		<lvr-restore-actions entityType="institution-person" [entityId]="draftId"></lvr-restore-actions>
	</lvr-default-actions>
</form>
